import {
  AMNIOTIC_FLUID_STATES,
  ANC_CONTACT_NUMBER_MAX,
  DELIVERY_OUTCOMES,
  BIRTH_NOTIFICATION_STATUSES,
  BIRTH_ORDER_MAX,
  BIRTH_WEIGHT_GRAMS,
  DANGER_SIGN_CODE_MAX,
  DANGER_SIGN_DISPLAY_MAX,
  DANGER_SIGN_LIST_MAX,
  DELIVERY_MODES,
  DOSE_PLACES,
  EDD_BASES,
  FEEDING_STATES,
  FETAL_MOVEMENTS,
  LABOUR_CLOSE_OUTCOMES,
  LABOUR_STATUSES,
  MATERNITY_NOTE_MAX,
  MATERNITY_RECORD_STATUSES,
  MOULDING_GRADES,
  OBSTETRIC_COUNT_MAX,
  PLACENTA_STATES,
  POSTNATAL_SUBJECTS,
  PREGNANCY_CLOSE_OUTCOMES,
  PREGNANCY_OUTCOMES,
  PREGNANCY_SEX_ALERT_RULE,
  PREGNANCY_STATUSES,
  SCHEDULE_AGE_DAYS_MAX,
  SCHEDULE_DOSE_MAX,
  SHORT_TEXT_MAX,
  YES_NO_NOT_RECORDED,
} from '@wodi/domain';
import { z } from 'zod';

import {
  actorSchema,
  ageSchema,
  codedRefSchema,
  codedValueSchema,
  isoDaySchema,
  reasonSchema,
} from './clinical-common.ts';
import { alertAcknowledgementSchema, vitalInputSchema, vitalSignSetSchema } from './triage.ts';

// Maternity and MCH (docs/plans/phase2-maternity-mch.md §3). Notes, danger-sign words and
// measured values are PHI: they travel only in bodies, never in URLs, logs or audit metadata.
// Every threshold, line, schedule and reference is a setting; an empty one reads "not set up".

const note = z.string().trim().min(1).max(MATERNITY_NOTE_MAX).nullable();
const shortText = z.string().trim().min(1).max(SHORT_TEXT_MAX).nullable();
const count = (min: number, max: number) => z.number().int().min(min).max(max);

export const dangerSignSchema = z.object({
  code: z.string().trim().min(1).max(DANGER_SIGN_CODE_MAX),
  display: z.string().trim().min(1).max(DANGER_SIGN_DISPLAY_MAX),
});

export type DangerSignView = z.infer<typeof dangerSignSchema>;

/** Vitals recorded with a maternity record, through the triage vitals capture and its rules. */
export const embeddedVitalsSchema = z.object({
  vitals: z
    .array(vitalInputSchema)
    .min(1)
    .max(20)
    .refine((v) => new Set(v.map((x) => x.key)).size === v.length, 'Each measurement once'),
  acknowledgements: z.array(alertAcknowledgementSchema).max(10).default([]),
});

export const gestationSchema = z.object({
  weeks: z.number().int().min(0),
  days: z.number().int().min(0).max(6),
  totalDays: z.number().int().min(0),
});

const recordedFields = {
  status: z.enum(MATERNITY_RECORD_STATUSES),
  statusReason: z.string().nullable(),
  recordedAt: z.iso.datetime(),
  recordedBy: actorSchema,
};

// --- Pregnancy ----------------------------------------------------------------------------------

const datingFields = {
  lmp: isoDaySchema.nullable(),
  edd: isoDaySchema.nullable(),
  eddBasis: z.enum(EDD_BASES).nullable(),
  gravida: count(1, OBSTETRIC_COUNT_MAX).nullable(),
  parity: count(0, OBSTETRIC_COUNT_MAX).nullable(),
  parityPlus: count(0, OBSTETRIC_COUNT_MAX).nullable(),
};

export const openPregnancyBodySchema = z.object({
  clientRequestId: z.uuid(),
  ...datingFields,
  /**
   * Answers to the interruptive alert for a patient not recorded as female (rule
   * `maternity.pregnancy_sex_not_female`, plan M25).
   */
  acknowledgements: z
    .array(z.object({ ruleId: z.literal(PREGNANCY_SEX_ALERT_RULE), reason: reasonSchema }))
    .max(1)
    .default([]),
});

export type OpenPregnancyBody = z.infer<typeof openPregnancyBodySchema>;

export const correctPregnancyBodySchema = z.object({
  ...datingFields,
  reason: reasonSchema,
  /** The version being corrected; a stale one is refused so two people can't overwrite. */
  version: z.number().int().min(1),
});

export type CorrectPregnancyBody = z.infer<typeof correctPregnancyBodySchema>;

export const closePregnancyBodySchema = z.object({
  outcome: z.enum(PREGNANCY_CLOSE_OUTCOMES),
  note,
});

export type ClosePregnancyBody = z.infer<typeof closePregnancyBodySchema>;

export const pregnancyVersionSchema = z.object({
  version: z.number().int().min(1),
  ...datingFields,
  changeReason: z.string().nullable(),
  changedAt: z.iso.datetime(),
  changedBy: actorSchema,
});

export const pregnancySchema = z.object({
  id: z.uuid(),
  patientId: z.uuid(),
  openedVisitId: z.uuid(),
  facilityName: z.string(),
  status: z.enum(PREGNANCY_STATUSES),
  outcome: z.enum(PREGNANCY_OUTCOMES).nullable(),
  ...datingFields,
  /** Weeks and days today (open pregnancies only); never a category. */
  gestationToday: gestationSchema.nullable(),
  version: z.number().int().min(1),
  openedAt: z.iso.datetime(),
  openedBy: actorSchema,
  closedAt: z.iso.datetime().nullable(),
  closeNote: z.string().nullable(),
  statusReason: z.string().nullable(),
  versions: z.array(pregnancyVersionSchema),
});

export type PregnancyView = z.infer<typeof pregnancySchema>;

// --- ANC ----------------------------------------------------------------------------------------

export const recordAncContactBodySchema = z.object({
  clientRequestId: z.uuid(),
  contactNumber: count(1, ANC_CONTACT_NUMBER_MAX),
  /** Clinical time. Absent means now. */
  contactAt: z.iso.datetime().optional(),
  fundalHeightCm: count(1, 60).nullable(),
  fetalHeartRate: count(1, 300).nullable(),
  presentation: shortText,
  fetalMovement: z.enum(FETAL_MOVEMENTS).nullable(),
  dangerSignCodes: z.array(z.string().max(DANGER_SIGN_CODE_MAX)).max(DANGER_SIGN_LIST_MAX),
  noDangerSigns: z.boolean(),
  nextVisitDate: isoDaySchema.nullable(),
  note,
  vitals: embeddedVitalsSchema.nullable(),
});

export type RecordAncContactBody = z.infer<typeof recordAncContactBodySchema>;

export const ancContactSchema = z.object({
  id: z.uuid(),
  pregnancyId: z.uuid(),
  visitId: z.uuid(),
  contactNumber: z.number().int(),
  contactAt: z.iso.datetime(),
  gestation: gestationSchema.nullable(),
  vitalSet: vitalSignSetSchema.nullable(),
  fundalHeightCm: z.number().int().nullable(),
  fetalHeartRate: z.number().int().nullable(),
  presentation: z.string().nullable(),
  fetalMovement: z.enum(FETAL_MOVEMENTS).nullable(),
  dangerSigns: z.array(dangerSignSchema),
  noDangerSigns: z.boolean(),
  nextVisitDate: isoDaySchema.nullable(),
  note: z.string().nullable(),
  ...recordedFields,
});

export type AncContactView = z.infer<typeof ancContactSchema>;

export const visitOrderLineSchema = z.object({
  id: z.uuid(),
  kind: z.enum(['lab', 'imaging', 'procedure', 'prescription']),
  name: z.string(),
  status: z.string(),
  orderedAt: z.iso.datetime(),
});

export const ancViewSchema = z.object({
  visit: z.object({
    id: z.uuid(),
    patientId: z.uuid(),
    status: z.enum(['open', 'closed', 'cancelled']),
    startedAt: z.iso.datetime(),
  }),
  patientAgeDays: z.number().int().nullable(),
  /** The patient's open pregnancy, or null: one must be opened first. */
  pregnancy: pregnancySchema.nullable(),
  contacts: z.array(ancContactSchema),
  /** The facility's ANC danger-sign list; empty means "not set up". */
  dangerSigns: z.array(dangerSignSchema),
  /** Tests and supplements ordered and prescribed on this visit, through the consultation. */
  orders: z.array(visitOrderLineSchema),
});

export type AncView = z.infer<typeof ancViewSchema>;

// --- Labour -------------------------------------------------------------------------------------

export const openLabourBodySchema = z.object({
  clientRequestId: z.uuid(),
  pregnancyId: z.uuid(),
  labourOnsetAt: z.iso.datetime().nullable(),
  membranesRupturedAt: z.iso.datetime().nullable(),
});

export type OpenLabourBody = z.infer<typeof openLabourBodySchema>;

export const labourOpenedSchema = z.object({ labourRecordId: z.uuid() });

/** Labour ended without a delivery here (plan M22): false labour, transferred out, other. */
export const closeLabourBodySchema = z.object({
  outcome: z.enum(LABOUR_CLOSE_OUTCOMES),
  note,
});

export type CloseLabourBody = z.infer<typeof closeLabourBodySchema>;

const observationFields = {
  cervicalDilationCm: z.number().min(0).max(10).nullable(),
  descentFifths: count(0, 5).nullable(),
  contractionsPer10Min: count(0, 10).nullable(),
  contractionSeconds: count(1, 300).nullable(),
  fetalHeartRate: count(1, 300).nullable(),
  amnioticFluid: z.enum(AMNIOTIC_FLUID_STATES).nullable(),
  moulding: z.enum(MOULDING_GRADES).nullable(),
};

export const recordLabourObservationBodySchema = z.object({
  clientRequestId: z.uuid(),
  observedAt: z.iso.datetime().optional(),
  ...observationFields,
  note,
  vitals: embeddedVitalsSchema.nullable(),
});

export type RecordLabourObservationBody = z.infer<typeof recordLabourObservationBodySchema>;

export const labourObservationSchema = z.object({
  id: z.uuid(),
  observedAt: z.iso.datetime(),
  ...observationFields,
  vitalSet: vitalSignSetSchema.nullable(),
  note: z.string().nullable(),
  ...recordedFields,
});

export type LabourObservationView = z.infer<typeof labourObservationSchema>;

const linePointSchema = z.object({ at: z.iso.datetime(), cm: z.number() });
const lineSchema = z.object({ start: linePointSchema, end: linePointSchema });

export const partographLinesSchema = z.discriminatedUnion('state', [
  z.object({ state: z.literal('not_configured') }),
  z.object({ state: z.literal('waiting_for_start') }),
  z.object({ state: z.literal('drawn'), alert: lineSchema, action: lineSchema }),
]);

export type PartographLinesView = z.infer<typeof partographLinesSchema>;

export const deliveryBabyBodySchema = z.object({
  birthOrder: count(1, BIRTH_ORDER_MAX),
  bornAt: z.iso.datetime(),
  outcome: z.enum(DELIVERY_OUTCOMES),
  sex: codedRefSchema.nullable(),
  birthWeightGrams: count(1, 9999).nullable(),
  apgar1: count(0, 10).nullable(),
  apgar5: count(0, 10).nullable(),
  note,
});

export const recordDeliveryBodySchema = z.object({
  clientRequestId: z.uuid(),
  deliveredAt: z.iso.datetime(),
  mode: z.enum(DELIVERY_MODES),
  theatreCaseId: z.uuid().nullable(),
  attendantUserId: z.uuid(),
  placentaDeliveredAt: z.iso.datetime().nullable(),
  placentaComplete: z.enum(PLACENTA_STATES),
  uterotonicGiven: z.enum(YES_NO_NOT_RECORDED),
  bloodLossMl: count(0, 10_000).nullable(),
  perineum: shortText,
  complications: note,
  babies: z.array(deliveryBabyBodySchema).min(1).max(BIRTH_ORDER_MAX),
});

export type RecordDeliveryBody = z.infer<typeof recordDeliveryBodySchema>;

export const deliveryBabySchema = z.object({
  id: z.uuid(),
  birthOrder: z.number().int(),
  bornAt: z.iso.datetime(),
  outcome: z.enum(DELIVERY_OUTCOMES),
  sex: codedValueSchema.nullable(),
  birthWeightGrams: z.number().int().nullable(),
  apgar1: z.number().int().nullable(),
  apgar5: z.number().int().nullable(),
  note: z.string().nullable(),
  /** The baby's record, once its birth notification created it (plan §5). */
  babyPatientId: z.uuid().nullable(),
  /** Not started (null), recorded, or entered in error on the birth notification itself. */
  birthNotification: z
    .object({ id: z.uuid(), status: z.enum(BIRTH_NOTIFICATION_STATUSES) })
    .nullable(),
});

export type DeliveryBabyView = z.infer<typeof deliveryBabySchema>;

export const deliverySchema = z.object({
  id: z.uuid(),
  labourRecordId: z.uuid().nullable(),
  deliveredAt: z.iso.datetime(),
  mode: z.enum(DELIVERY_MODES),
  theatreCase: z.object({ id: z.uuid(), procedureName: z.string() }).nullable(),
  attendant: actorSchema,
  placentaDeliveredAt: z.iso.datetime().nullable(),
  placentaComplete: z.enum(PLACENTA_STATES),
  uterotonicGiven: z.enum(YES_NO_NOT_RECORDED),
  bloodLossMl: z.number().int().nullable(),
  perineum: z.string().nullable(),
  complications: z.string().nullable(),
  babies: z.array(deliveryBabySchema),
  ...recordedFields,
});

export type DeliveryView = z.infer<typeof deliverySchema>;

/**
 * Starts a live-born baby's birth notification from the delivery (plan §5). The time, the
 * mother's visit and the birth order come from the delivery, and so do the sex and weight it
 * recorded; these two fields only fill what the delivery left empty (null: take the
 * delivery's). A different value is refused.
 */
export const deliveryBirthNotificationBodySchema = z.object({
  clientRequestId: z.uuid(),
  attendantUserId: z.uuid(),
  sex: codedRefSchema.nullable(),
  birthWeightGrams: z
    .number()
    .int()
    .min(BIRTH_WEIGHT_GRAMS.min)
    .max(BIRTH_WEIGHT_GRAMS.max)
    .nullable(),
});

export type DeliveryBirthNotificationBody = z.infer<typeof deliveryBirthNotificationBodySchema>;

// --- Postnatal ----------------------------------------------------------------------------------

export const recordPostnatalCheckBodySchema = z.object({
  clientRequestId: z.uuid(),
  /** An open visit of the mother at this facility. */
  visitId: z.uuid(),
  subject: z.enum(POSTNATAL_SUBJECTS),
  deliveryBabyId: z.uuid().nullable(),
  checkedAt: z.iso.datetime().optional(),
  feeding: z.enum(FEEDING_STATES).nullable(),
  dangerSignCodes: z.array(z.string().max(DANGER_SIGN_CODE_MAX)).max(DANGER_SIGN_LIST_MAX),
  noDangerSigns: z.boolean(),
  babyWeightGrams: count(1, 9999).nullable(),
  note,
  /** The mother's vitals only; a baby's are taken on the baby's own visit (M11). */
  vitals: embeddedVitalsSchema.nullable(),
});

export type RecordPostnatalCheckBody = z.infer<typeof recordPostnatalCheckBodySchema>;

export const postnatalCheckSchema = z.object({
  id: z.uuid(),
  deliveryId: z.uuid(),
  subject: z.enum(POSTNATAL_SUBJECTS),
  deliveryBabyId: z.uuid().nullable(),
  birthOrder: z.number().int().nullable(),
  visitId: z.uuid(),
  checkedAt: z.iso.datetime(),
  vitalSet: vitalSignSetSchema.nullable(),
  babyWeightGrams: z.number().int().nullable(),
  feeding: z.enum(FEEDING_STATES).nullable(),
  dangerSigns: z.array(dangerSignSchema),
  noDangerSigns: z.boolean(),
  note: z.string().nullable(),
  ...recordedFields,
});

export type PostnatalCheckView = z.infer<typeof postnatalCheckSchema>;

// --- Labour record and the patient's maternity page --------------------------------------------

export const labourSummarySchema = z.object({
  id: z.uuid(),
  pregnancyId: z.uuid(),
  admissionId: z.uuid(),
  visitId: z.uuid(),
  status: z.enum(LABOUR_STATUSES),
  statusReason: z.string().nullable(),
  closeOutcome: z.enum(LABOUR_CLOSE_OUTCOMES).nullable(),
  closeNote: z.string().nullable(),
  openedAt: z.iso.datetime(),
  openedBy: actorSchema,
  labourOnsetAt: z.iso.datetime().nullable(),
  membranesRupturedAt: z.iso.datetime().nullable(),
  wardName: z.string().nullable(),
  bedLabel: z.string().nullable(),
});

export type LabourSummaryView = z.infer<typeof labourSummarySchema>;

export const openVisitOptionSchema = z.object({
  id: z.uuid(),
  visitNumber: z.string(),
  type: z.string(),
  startedAt: z.iso.datetime(),
});

export const labourRecordViewSchema = z.object({
  labour: labourSummarySchema,
  patientId: z.uuid(),
  patientAgeDays: z.number().int().nullable(),
  pregnancy: pregnancySchema,
  observations: z.array(labourObservationSchema),
  partograph: partographLinesSchema,
  delivery: deliverySchema.nullable(),
  postnatalChecks: z.array(postnatalCheckSchema),
  /** Theatre cases on this stay that a caesarean section may be linked to. */
  theatreCases: z.array(
    z.object({
      id: z.uuid(),
      procedureName: z.string(),
      scheduledStart: z.iso.datetime(),
      status: z.string(),
    }),
  ),
  /** Who may be named as the attendant: staff who may record maternity at this facility. */
  attendants: z.array(actorSchema),
  /** The mother's open visits here, for postnatal checks. */
  openVisits: z.array(openVisitOptionSchema),
  postnatalMotherDangerSigns: z.array(dangerSignSchema),
  postnatalBabyDangerSigns: z.array(dangerSignSchema),
  /** The tenant's sex value set, for each baby on the delivery form. */
  sexOptions: z.array(codedValueSchema),
});

export type LabourRecordView = z.infer<typeof labourRecordViewSchema>;

export const pregnancyRecordSchema = z.object({
  pregnancy: pregnancySchema,
  ancContacts: z.array(ancContactSchema),
  labours: z.array(labourSummarySchema),
  deliveries: z.array(deliverySchema),
  postnatalChecks: z.array(postnatalCheckSchema),
});

export type PregnancyRecordView = z.infer<typeof pregnancyRecordSchema>;

export const maternityViewSchema = z.object({
  patientId: z.uuid(),
  /** Pregnancies the reader may see, newest first; at most one is open. */
  pregnancies: z.array(pregnancyRecordSchema),
  /** The patient's open visits here, to open a pregnancy or record a contact on. */
  openVisits: z.array(openVisitOptionSchema),
  /** The patient's current stay here, if admitted, to open a labour record on. */
  admission: z.object({ id: z.uuid(), wardName: z.string().nullable() }).nullable(),
});

export type MaternityView = z.infer<typeof maternityViewSchema>;

export const labourWardRowSchema = z.object({
  labourRecordId: z.uuid(),
  status: z.enum(LABOUR_STATUSES),
  openedAt: z.iso.datetime(),
  lastObservationAt: z.iso.datetime().nullable(),
  wardName: z.string().nullable(),
  bedLabel: z.string().nullable(),
  /** Null when the reader may not see this visit (sensitivity labels). */
  patient: z
    .object({
      id: z.uuid(),
      displayName: z.string(),
      patientNumber: z.string(),
      age: ageSchema.nullable(),
    })
    .nullable(),
});

export const labourWardSchema = z.object({ rows: z.array(labourWardRowSchema) });

export type LabourWardView = z.infer<typeof labourWardSchema>;

// --- Immunisation and growth --------------------------------------------------------------------

export const recordImmunisationBodySchema = z.object({
  clientRequestId: z.uuid(),
  vaccineId: z.uuid(),
  doseNumber: count(1, SCHEDULE_DOSE_MAX),
  /** Clinical time. Absent means now. For a dose given elsewhere, the card's date at noon. */
  givenAt: z.iso.datetime().optional(),
  givenWhere: z.enum(DOSE_PLACES),
  /** Here only: a chosen batch; null means earliest expiry first (FEFO). */
  batchId: z.uuid().nullable(),
  batchOverrideReason: reasonSchema.nullable(),
  /** Elsewhere only: the batch written on the card, as entered, when the card has one. */
  batchNumberAsEntered: shortText,
  site: shortText,
  note,
});

export type RecordImmunisationBody = z.infer<typeof recordImmunisationBodySchema>;

export const immunisationDoseSchema = z.object({
  id: z.uuid(),
  visitId: z.uuid(),
  vaccine: z.object({
    id: z.uuid(),
    display: z.string(),
    code: z.string(),
    codeSystem: z.string(),
    codeSystemVersion: z.string().nullable(),
  }),
  doseNumber: z.number().int(),
  givenAt: z.iso.datetime(),
  givenWhere: z.enum(DOSE_PLACES),
  batchNumber: z.string().nullable(),
  storeName: z.string().nullable(),
  charged: z.boolean(),
  site: z.string().nullable(),
  note: z.string().nullable(),
  ...recordedFields,
});

export type ImmunisationDoseView = z.infer<typeof immunisationDoseSchema>;

export const recordGrowthBodySchema = z.object({
  clientRequestId: z.uuid(),
  effectiveAt: z.iso.datetime().optional(),
  /** Decimal text as typed, in kg and cm; at least one. */
  weightKg: z.string().trim().min(1).max(12).nullable(),
  heightCm: z.string().trim().min(1).max(12).nullable(),
  acknowledgements: z.array(alertAcknowledgementSchema).max(10).default([]),
});

export type RecordGrowthBody = z.infer<typeof recordGrowthBodySchema>;

const growthPointSchema = z.object({
  setId: z.uuid(),
  at: z.iso.datetime(),
  value: z.number(),
  ageDays: z.number().int().nullable(),
});

export const dueEntrySchema = z.object({
  vaccine: z.object({ id: z.uuid(), display: z.string() }),
  doseNumber: z.number().int(),
  dueOn: isoDaySchema,
  state: z.enum(['given', 'due', 'upcoming']),
});

export const vaccineStockSchema = z.object({
  vaccineId: z.uuid(),
  batches: z.array(
    z.object({
      batchId: z.uuid(),
      batchNumber: z.string(),
      expiryDate: isoDaySchema.nullable(),
      quantity: z.number().int(),
    }),
  ),
});

export const childHealthViewSchema = z.object({
  patientId: z.uuid(),
  birthDate: isoDaySchema.nullable(),
  patientAgeDays: z.number().int().nullable(),
  openVisits: z.array(openVisitOptionSchema),
  doses: z.array(immunisationDoseSchema),
  dueList: z.discriminatedUnion('state', [
    z.object({ state: z.literal('not_configured') }),
    z.object({ state: z.literal('no_birth_date') }),
    z.object({ state: z.literal('listed'), entries: z.array(dueEntrySchema) }),
  ]),
  growth: z.object({
    /** TODO(clinical-verify) M19: always not configured until a reference is approved. */
    reference: z.literal('not_configured'),
    weight: z.array(growthPointSchema),
    height: z.array(growthPointSchema),
  }),
  vaccines: z.array(
    z.object({ id: z.uuid(), display: z.string(), code: z.string(), charged: z.boolean() }),
  ),
  /** The facility's immunisation store and its usable batches, or null when not set up. */
  store: z
    .object({ id: z.uuid(), name: z.string(), stock: z.array(vaccineStockSchema) })
    .nullable(),
});

export type ChildHealthView = z.infer<typeof childHealthViewSchema>;

// --- Settings -----------------------------------------------------------------------------------

export const partographSettingsSchema = z.object({
  alertStartCm: z.number().min(0).max(10).nullable(),
  alertRateCmPerHour: z.number().gt(0).max(10).nullable(),
  actionOffsetHours: z.number().gt(0).max(24).nullable(),
});

export const maternitySettingsBodySchema = z.object({
  ancDangerSigns: z.array(dangerSignSchema).max(DANGER_SIGN_LIST_MAX),
  postnatalMotherDangerSigns: z.array(dangerSignSchema).max(DANGER_SIGN_LIST_MAX),
  postnatalBabyDangerSigns: z.array(dangerSignSchema).max(DANGER_SIGN_LIST_MAX),
  partograph: partographSettingsSchema,
  immunisationStoreId: z.uuid().nullable(),
  /**
   * TODO(clinical-verify) M23: weeks past the EDD after which the banner shows a passive
   * "Past EDD, check pregnancy status" cue. Null: no cue.
   */
  pastEddCueWeeks: count(1, 52).nullable(),
  version: z.number().int().min(0),
});

export type MaternitySettingsBody = z.infer<typeof maternitySettingsBodySchema>;

export const maternitySettingsSchema = z.object({
  ancDangerSigns: z.array(dangerSignSchema),
  postnatalMotherDangerSigns: z.array(dangerSignSchema),
  postnatalBabyDangerSigns: z.array(dangerSignSchema),
  partograph: partographSettingsSchema,
  immunisationStore: z.object({ id: z.uuid(), name: z.string() }).nullable(),
  pastEddCueWeeks: z.number().int().nullable(),
  /** 0 until first saved. */
  version: z.number().int().min(0),
  stores: z.array(z.object({ id: z.uuid(), name: z.string() })),
});

export type MaternitySettingsView = z.infer<typeof maternitySettingsSchema>;

export const vaccineBodySchema = z.object({
  /** A medicine stock item from the catalogue. */
  serviceItemId: z.uuid(),
  code: z.string().trim().min(1).max(64),
  codeSystem: z.string().trim().min(1).max(200),
  /** The code system's version, when known; copied onto each dose (codes travel with data). */
  codeSystemVersion: z.string().trim().min(1).max(64).nullable(),
  display: z.string().trim().min(1).max(200),
  charged: z.boolean(),
  status: z.enum(['active', 'inactive']),
});

export type VaccineBody = z.infer<typeof vaccineBodySchema>;

export const vaccineSchema = vaccineBodySchema.extend({
  id: z.uuid(),
  itemName: z.string(),
});

export type VaccineView = z.infer<typeof vaccineSchema>;

export const scheduleEntryBodySchema = z.object({
  vaccineId: z.uuid(),
  doseNumber: count(1, SCHEDULE_DOSE_MAX),
  dueAgeDays: count(0, SCHEDULE_AGE_DAYS_MAX),
});

export const immunisationScheduleBodySchema = z.object({
  entries: z.array(scheduleEntryBodySchema).max(200),
});

export type ImmunisationScheduleBody = z.infer<typeof immunisationScheduleBodySchema>;

export const vaccinesViewSchema = z.object({
  vaccines: z.array(vaccineSchema),
  /** Empty by default: TODO(clinical-verify) M16. */
  schedule: z.array(scheduleEntryBodySchema),
  /** Medicine stock items that may be set up as vaccines. */
  items: z.array(z.object({ id: z.uuid(), name: z.string(), code: z.string() })),
});

export type VaccinesView = z.infer<typeof vaccinesViewSchema>;
